<template>
  <div class="terms">
    <div class="header mt-2">
      <div />
      <div class="h1 flex justify-content-center">
        Cookie Policy
      </div>
      <div class="flex justify-content-end">
        <AuButton
          center
          width="100px"
          @click="openDoc"
        >
          Download
        </AuButton>
      </div>
    </div>
    <div class="h5 mt-5 ms-4 me-4">
      <p><strong>Version:</strong> 1.0</p>
      <p><strong>Last Updated:</strong> 2.02.2026</p>

      <h3 class="mt-4">
        1. Introduction
      </h3>
      <p>
        This Cookie Policy (hereinafter -- "the Policy") explains how
        [RWA.capital legal entity name] (hereinafter -- "the Company", "we",
        "us", or "our"), incorporated in the Dubai International Financial
        Centre ("DIFC"), uses cookies and similar technologies when you visit
        our website and related online services, including any user dashboards
        (together, the "Platform").
      </p>
      <p>
        We offer access to the Platform to a worldwide audience. Depending on
        your location, cookies may be governed by additional rules (e.g., the
        EU/UK ePrivacy rules). Please, also see our Privacy Policy.
      </p>

      <h3 class="mt-4">
        2. What are cookies?
      </h3>
      <p>
        Cookies are small text files of information, which are created/updated
        when you visit our website, and stored on your web browser. They help
        the Platform recognize your device, remember respective preferences, and
        perform securely and efficiently. Similar technologies
        (pixels/SDKs/local storage) may be employed for the same purposes and
        are treated as "cookies" in this Cookie Policy.
      </p>

      <h3 class="mt-4">
        3. How we use cookies
      </h3>
      <p>
        We use cookies to:
      </p>
      <ul>
        <li>authenticate you and keep you signed in;</li>
        <li>enable core Platform features (including access to secure areas);</li>
        <li>protect the Platform and users (fraud/security, incident prevention); and</li>
        <li>measure and improve performance and user experience.</li>
      </ul>
      <p>
        Certain cookies may process personal data such as the IP address and
        device identifiers.
      </p>

      <h3 class="mt-4">
        4. Categories of cookies we use
      </h3>
      <p>
        An up‑to‑date view of cookies in use is available via our cookie banner
        / settings panel.
      </p>

      <h4 class="mt-3">
        4.1 Essential cookies
      </h4>
      <p>
        These cookies are required to ensure due performance of the Platform,
        and they cannot be switched off in our settings. Should you block them,
        certain parts of the Platform may fail to function.
      </p>
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Cookie (example)</th>
              <th>Purpose</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>RWA_SessionID</td>
              <td>Identifies your session during your visit</td>
              <td>Session</td>
            </tr>
            <tr>
              <td>__auth_token</td>
              <td>Ensures security during navigation through pages</td>
              <td>Session</td>
            </tr>
            <tr>
              <td>cookie_consent_status</td>
              <td>Stores your cookie choices and prevents repeated pop‑ups</td>
              <td>30 days</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        Essential cookies may also be set by vendors that provide
        infrastructure, security, and compliance flows you initiate (including
        KYC/AML providers). Where such cookies are absolutely necessary to
        provide the respectively requested service, they will be treated as
        essential.
      </p>

      <h4 class="mt-3">
        4.2 Functionality cookies
      </h4>
      <p>
        These cookies remember your settings and enable enhanced functionality.
      </p>
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Cookie (example)</th>
              <th>Purpose</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>pref_lang</td>
              <td>Remembers the selected language</td>
              <td>1 year</td>
            </tr>
            <tr>
              <td>pref_currency</td>
              <td>Remembers the preferred display currency</td>
              <td>1 year</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h4 class="mt-3">
        4.3 Performance & Analytics cookies
      </h4>
      <p>
        These cookies help us gain an understanding of how visitors use the
        Platform so we can improve it.
      </p>
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Cookie (example)</th>
              <th>Provider / Purpose</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>_ga</td>
              <td>Google Analytics: distinguishes users</td>
              <td>2 years</td>
            </tr>
            <tr>
              <td>_gid</td>
              <td>Google Analytics: distinguishes users</td>
              <td>24 hours</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h4 class="mt-3">
        4.4 Third‑party & Marketing cookies
      </h4>
      <p>
        With your consent obtained (where required), we may use third‑party
        cookies/pixels to measure advertising and, where included, for targeted
        advertising and retargeting. These providers may set their own cookies
        and process data subject to their privacy policies.
      </p>
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Cookie (example)</th>
              <th>Provider / Purpose</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>IDE</td>
              <td>Google/DoubleClick: advertising measurement & targeting</td>
              <td>1 year</td>
            </tr>
            <tr>
              <td>_fbp</td>
              <td>Meta/Facebook: ad delivery and measurement</td>
              <td>3 months</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 class="mt-4">
        5. Cookie preference management
      </h3>
      <p>
        When visiting the Platform for the first time, you will see a cookie
        banner that allows you to accept, reject, or customize non‑essential
        cookies.
      </p>
      <p>
        We place non‑essential cookies only after we obtain your consent.
        Essential cookies are always enabled because they are employed to ensure
        secure use of the Platform.
      </p>
      <p>
        Your consent can be changed or withdrawn at any time via the "Cookie
        Settings" link in the Platform footer (or through reopening the banner).
        Besides, you can control cookies through your browser settings (e.g.,
        delete the existing cookies or block any extra ones).
      </p>
      <p>
        In the event you are in the EEA/UK, rejecting non‑essential cookies will
        not prevent you from using the Platform, yet certain features and
        customization may be limited.
      </p>

      <h3 class="mt-4">
        6. Updates to this Cookie Policy
      </h3>
      <p>
        This Cookie Policy may be updated from time to time. The "Last Updated"
        date above indicates when it was last revised.
      </p>

      <h3 class="mt-4">
        7. Contact us
      </h3>
      <p>
        For any questions regarding cookies, please use this email:
        <a href="mailto:privacy@rwa.capital">privacy@rwa.capital</a>
      </p>
      <p>
        Address: [Registered address in DIFC, Dubai, UAE]
      </p>
    </div>
  </div>
</template>

<script setup>
import AuButton from "@/components/extended/AuButton.vue";
import { checkAccess, scope } from "@/help/accessData";
import { onMounted } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const openDoc = () => {
  window.open("/docs/Cookie_policy_rwa.pdf", "_blank");
};
onMounted(() => {
  if (checkAccess(scope.notRwa)) {
    router.push({ name: "page not found" });
  }
});
</script>

<style scoped lang="scss">
ul {
  margin-left: 1.5rem;
  margin-bottom: 1rem;
}

.table {
  margin-bottom: 1rem;

  th, td {
    padding: 0.75rem;
    vertical-align: top;
    background-color: var(--main-color);
    color: var(--text-color);
    border: 1px solid var(--text-color);
  }

  thead th {
    border: 1px solid var(--text-color);
  }
}

.terms {
  background-color: var(--main-color);
  padding-bottom: 10px;

  .header {
    display: grid;
    grid-template-columns: 33% auto 33%;
    padding-right: 20px;
  }
}

a {
  color: var(--color-green);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>